// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// react router
import { Link, useLocation, useNavigate } from 'react-router';

// mui
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import { default as Grid } from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { default as MUILink } from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import Block from '@mui/icons-material/Block';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import { default as GoogleIcon } from '@mui/icons-material/Google';
import { default as InfoIcon } from '@mui/icons-material/Info';
import { default as MicrosoftIcon } from '@mui/icons-material/Microsoft';
import ScheduleSend from '@mui/icons-material/ScheduleSend';
import Undo from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';

// dayjs
import dayjs from 'dayjs';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { clearOtherData } from 'store/features/snackbarSlice';

import { useSnackbar } from 'notistack';

// project constants
import { bookingStatuses } from 'constants/bookings';
import userTypes from 'constants/userTypes';
import { managerLocationOptions } from 'constants/managerOptions';
import { calendarEventTypes } from 'constants/calendar';

// project utils
import { useGetEntityName } from 'hooks-ts/useGetEntity';
import {
  dateIsValidPerManagerSchedules,
  dayIsUnavailable,
  filterManagerSchedulesByLocation,
  getBookingStatus,
  getCreatedFrom,
  getDateWithCustomTime,
  getEarliestAvailableDate,
  getPreferredTimeFrameLabel,
  getTimeSlotInterval,
  isTimeSlotInLocationFuture
} from 'utils/booking';
import handleError from 'utils-ts/handle-error';
import callAzureFunction from 'utils-ts/call-azure-function';
import companyLabelBuilder from 'utils-ts/company';
import callAzureFunctionPublic from 'utils-ts/call-azure-function-public';
import useAppDataSource from 'hooks/useAppDataSource';
import useCancellationInitiatorPrompt from 'hooks/useCancellationInitiatorPrompt';
import { useTrackRecentlyViewedItem } from 'components-ts/recently-viewed/RecentlyViewedTracker';
import { useGeneralInvoicingAndPaymentsEnabled } from 'hooks/usePayments';

// project components
import BookingsCollisionDialog from 'ui-component/dialogs/BookingsCollisionDialog';
import getSnackbarMessageType from 'utils/snackbar-message';
import HtmlTooltip from 'components-ts/tooltips/HtmlTooltip';
import AutocompleteWithInfiniteScroll from 'components-ts/autocompletes/AutocompleteWithInfiniteScroll';
import TimezoneAutocompleteNzAu from 'components-ts/autocompletes/TimezoneAutocompleteNzAu';
import EmailBookingDialog from 'views/shared/manage-bookings/EmailBookingDialog';
import EmailEventLogsField from 'views/shared/manage-bookings/EmailEventLogsField';
import { CustomStaticDatePicker, CustomTimePicker } from 'components-ts/pickers/DateTimePickers';
import ChangeBookingTypeDialog from './ChangeBookingTypeDialog';
import ScheduledNotificationsDialog from 'views/shared/manage-bookings/dialogs/ScheduledNotificationsDialog';
import ActivityLogsDialog from './dialogs/ActivityLogsDialog';
import ManagerScheduleConflictDialog from 'ui-component/dialogs/ManagerScheduleConflictDialog';
import BookingInvoicesDialog from 'components-ts/sales-and-invoicing/BookingInvoicesDialog';
import {
  CORE_PERMISSIONS,
  INVOICE_CREATION_SOURCE,
  INVOICE_STATUS,
  INVOICE_STATUS_ACTION,
  getIsoDate,
  affiliateBookingStatuses,
  LOCATION_TYPE
} from '@a1/shared';
import VoidDialog from 'components-ts/sales-and-invoicing/VoidDialog';
import LocationUnavailableContent from 'components-ts/dialogs/LocationUnavailableContent';
import { TipTapEditor } from 'ui-component/TipTapEditor';
import InvoicePaymentsDialog from 'components-ts/sales-and-invoicing/InvoicePaymentsDialog';
import { useDebounce } from 'use-debounce';
import PaginatedMenu from 'components-ts/menus/PaginatedMenu';
import BookingStatusPicker from 'components-ts/booking-custom-statuses/BookingStatusPicker';

const CustomTimeTextField = (params) => (
  <TextField
    {...params}
    variant="outlined"
    slotProps={{
      input: {
        ...params.InputProps,
        startAdornment: <InputAdornment position="start">Choose time</InputAdornment>
      }
    }}
  />
);

//= =============================|| ViewBookingDialog ||==============================//

/**
 * Booking dialog that shows details for a booking and allows editing
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.setOpen - Function to set the open state of the dialog
 * @param {string} props.bookingId - ID of the booking to display
 * @param {Function} props.setBookings - Function to update the bookings list after booking is updated
 * @param {Function} [props.getBookings] - Optional function to refresh bookings (not passed by BookingActionSnackbar)
 * @param {boolean} props.enableStatusUpdate - Whether the status can be updated
 * @param {boolean} [props.isReadOnly=false] - Whether the dialog is read-only (true when opened from BookingActionSnackbar)
 * @param {string} [props.mode] - Calendar mode, passed through from calendar
 * @returns {JSX.Element} The ViewBookingDialog component
 */
const ViewBookingDialog = ({ open, setOpen, bookingId, setBookings, getBookings, enableStatusUpdate, isReadOnly = false, mode }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const matchesDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const { appDataSourceId, isAppDataSourceRemote } = useAppDataSource();
  const generalInvoicingAndPaymentsEnabled = useGeneralInvoicingAndPaymentsEnabled();

  const bookingEntityName = useGetEntityName('booking');
  const bookingsEntityName = useGetEntityName('bookings');
  const managerEntityName = useGetEntityName('manager');
  const respondentEntityName = useGetEntityName('respondent');
  const surveysEntityName = useGetEntityName('surveys');

  const appConfig = useSelector((state) => state.appConfig?.data);
  const { enableTimeSlotInterval, defaultTimeSlotInterval } = useSelector((state) => state.appConfig?.data.bookings) || {};
  const bookingConfig = appConfig?.bookings;
  const user = useSelector((state) => state.user.data);
  const permissions = useSelector((state) => state.user.permissions);
  const canUpdateBookingCompanies = permissions.includes(CORE_PERMISSIONS.MANAGE_BOOKING_COMPANIES);
  const enableIinsightCaseNumQuery = appConfig?.integrations?.iinsight?.enableIinsightCaseNumQuery;
  const companiesFeatureEnabled = appConfig?.others?.companies;
  const iinsightCaseCreationEnabled = appConfig?.integrations.iinsight?.caseCreationConfig?.enabled;
  const requireAssignedManager = Boolean(bookingConfig?.requireAssignedManager);

  const pageLocation = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [booking, setBooking] = useState({});
  const [loadingBooking, setLoadingBooking] = useState(true);
  const [updatingBooking, setUpdatingBooking] = useState(false);
  const [viewEmailBookingDialogOpen, setViewEmailBookingDialogOpen] = useState(false);
  const [openChangeBookingTypeDialog, setOpenChangeBookingTypeDialog] = useState(false);

  const [managerHasGoogleCalendar, setManagerHasGoogleCalendar] = useState(false);
  const [managerHasOutlookCalendar, setManagerHasOutlookCalendar] = useState(false);

  const [openScheduledNotificationsDialog, setOpenScheduledNotificationsDialog] = useState(false);
  const [openActivityLogsDialog, setOpenActivityLogsDialog] = useState(false);

  const [openBookingInvoicesDialog, setOpenBookingInvoicesDialog] = useState(false);

  const {
    Manager: manager,
    Respondent: respondent,
    Location: currentLocation,
    Service: currentService,
    Company: currentCompany,
    Invoices,
    anyManagerAtLocation,
    respondentAvailabilityIsKnown,
    affiliateSelected,
    date,
    startHours,
    startMinutes,
    endHours,
    endMinutes,
    status,
    insightCaseId,
    affiliateLocation,
    specifiedAffiliateTimezone,
    isSelectFromSystemAffiliateLocations,
    preferredTimeFrame,
    respondentConfirmationNotifications,
    affiliateNotes: bookingAffiliateNotes,
    createdAt,
    updatedAt
  } = booking;
  const bookingCreatedFrom = useMemo(() => getCreatedFrom(booking), [booking]);

  const [invoices, setInvoices] = useState([]);
  const [openInvoicePaymentsDialog, setOpenInvoicePaymentsDialog] = useState(false);

  // change status
  const [newStatus, setNewStatus] = useState(null);
  const [pendingCustomStatus, setPendingCustomStatus] = useState(null);

  // Cached so the collision retry (attemptUpdateBooking({ unsafe: true })) reuses the answer
  // instead of asking a second time.
  const [resolvedCancellationAnswer, setResolvedCancellationAnswer] = useState(null);
  const { resolveCancellationInitiator, cancellationInitiatorDialog } = useCancellationInitiatorPrompt();

  // change service
  const [changeService, setChangeService] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [disableChangeService, setDisableChangeService] = useState(false);

  // change location
  const [changeLocation, setChangeLocation] = useState(false);
  const [newLocation, setNewLocation] = useState(null);
  const [disableChangeLocation, setDisableChangeLocation] = useState(false);
  const location = isSelectFromSystemAffiliateLocations && affiliateSelected ? null : newLocation || currentLocation;
  const [currentLocationUnavailableDays, setCurrentLocationUnavailableDays] = useState(null);

  // Companies
  const [newCompany, setNewCompany] = useState(null);
  const [currentLocalCompany, setCurrentLocalCompany] = useState(null);
  const [newLocalCompany, setNewLocalCompany] = useState(null);

  /**
   * Invoices
   */
  const [openVoidDialog, setOpenVoidDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // affiliate notes
  const [affiliateNotes, setAffiliateNotes] = useState('');
  const [viewAffiliateNotes, setViewAffiliateNotes] = useState(false);
  const debounceAffiliateNotes = useDebounce(affiliateNotes, 500)[0];
  const [affiliateNotesHasChanges, setAffiliateNotesHasChanges] = useState(false);

  useEffect(() => {
    setAffiliateNotesHasChanges(false);
    if (debounceAffiliateNotes !== (bookingAffiliateNotes ?? '')) {
      setAffiliateNotesHasChanges(true);
    }
  }, [debounceAffiliateNotes, bookingAffiliateNotes]);

  useEffect(() => {
    setSelectedService(currentService);
    // When the user switches the toggle for changing service or location,
    // we display the current service in the service autocomplete.
    // We also display the service autocomplete in change location
    // that's why we need to reset in that area.
  }, [currentService, changeService, changeLocation]);

  const bookingPageInvoice = useMemo(
    () => invoices?.find((inv) => inv.creationSource === INVOICE_CREATION_SOURCE.BOOKING_PAGE),
    [invoices]
  );
  const [invoiceError, setInvoiceError] = useState(null);

  const handleVoid = async (invoiceId) => {
    try {
      setLoading(true);

      const response = await callAzureFunction({
        url: `invoice-status-change`,
        method: 'post',
        data: {
          invoiceId,
          action: INVOICE_STATUS_ACTION.VOID
        }
      });

      await getBookings();

      const updatedInvoice = response.data;
      setInvoices((prev) => prev.map((inv) => (inv.id === updatedInvoice.id ? updatedInvoice : inv)));
      setInvoiceError(null);

      enqueueSnackbar({
        variant: 'success',
        message: `Invoice ${invoiceId} has been voided successfully.`,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } catch (error) {
      enqueueSnackbar({
        variant: 'error',
        message: handleError(error),
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } finally {
      setLoading(false);
      setOpenVoidDialog(false);
    }
  };

  useEffect(() => {
    setInvoices(Invoices);
  }, [Invoices]);

  useEffect(() => {
    if (booking.localCompanyName && booking.localCompanyId) {
      const company = { id: booking.localCompanyId, name: booking.localCompanyName };
      setCurrentLocalCompany(company);
      setNewLocalCompany(company);
    } else {
      setCurrentLocalCompany(null);
      setNewLocalCompany(null);
    }
  }, [booking.localCompanyId, booking.localCompanyName]);

  useEffect(() => {
    if (currentCompany) {
      setNewCompany(currentCompany);
    }
  }, [currentCompany]);

  // change manager
  const [currentManager, setCurrentManager] = useState(manager);
  const [changeManager, setChangeManager] = useState(false);
  const [newManagerOption, setNewManagerOption] = useState(null);
  const [newManager, setNewManager] = useState(null);
  const [newManagers, setNewManagers] = useState([]);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [loadingAvailableManagers, setLoadingAvailableManagers] = useState(false);
  const [disableChangeManager, setDisableChangeManager] = useState(false);

  const [anchorElAddManagersMenu, setAnchorElAddManagersMenu] = useState(null);

  // change date/time (reschedule)
  const [changeDateTime, setChangeDateTime] = useState(false);
  const [newDate, setNewDate] = useState(date ? dayjs(date) : dayjs());
  const [newTimeSlot, setNewTimeSlot] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingTimeSlotsCount, setLoadingTimeSlotsCount] = useState(0);
  const startLoadingTimeSlots = () => setLoadingTimeSlotsCount((count) => count + 1);
  const stopLoadingTimeSlots = () => setLoadingTimeSlotsCount((count) => Math.max(count - 1, 0));

  useEffect(() => {
    if (!booking?.date) {
      setNewDate(dayjs());
      return;
    }

    const parsedBookingDate = dayjs(booking.date);
    setNewDate(parsedBookingDate.isValid() ? parsedBookingDate : null);
  }, [booking?.date]);

  const loadingTimeSlots = loadingTimeSlotsCount > 0;

  const [disableChangeDateTime, setDisableChangeDateTime] = useState(false);
  // change date/time (affiliate)
  const [changeDateTimeAffiliate, setChangeDateTimeAffiliate] = useState(false);
  const [oldTimeSlot, setOldTimeSlot] = useState(null);
  const defaultStartTime = dayjs().hour(7).minute(0).toDate();

  // Affiliate Location
  const [affiliateLocationState, setAffiliateLocationState] = useState({
    // Holds the selected location object from the Autocomplete
    systemLocation: null,
    // Holds the free-text value for a manually specified location
    manualLocationText: affiliateLocation || '',
    // Holds the selected IANA timezone for a manually specified location
    manualTimezone: specifiedAffiliateTimezone || '',
    // Toggles between Autocomplete and TextField
    isManualEntry: !isSelectFromSystemAffiliateLocations
  });

  // When the booking data loads, initialize the state
  useEffect(() => {
    setAffiliateLocationState({
      systemLocation: isSelectFromSystemAffiliateLocations ? currentLocation : null,
      manualLocationText: affiliateLocation || '',
      manualTimezone: specifiedAffiliateTimezone || '',
      isManualEntry: !isSelectFromSystemAffiliateLocations
    });
  }, [bookingId, affiliateLocation, specifiedAffiliateTimezone, isSelectFromSystemAffiliateLocations, currentLocation]);

  // When a system location is selected, clear the manual text field.
  useEffect(() => {
    if (affiliateLocationState.systemLocation) {
      setAffiliateLocationState((prevState) => ({ ...prevState, manualLocationText: '' }));
    }
  }, [affiliateLocationState.systemLocation]);

  // When the user starts typing in the manual field, clear the system location selection.
  useEffect(() => {
    if (affiliateLocationState.manualLocationText) {
      setAffiliateLocationState((prevState) => ({ ...prevState, systemLocation: null }));
    }
  }, [affiliateLocationState.manualLocationText]);

  // error
  const [error, setError] = useState(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    if (open) {
      setHasAttemptedSubmit(false);
    }
  }, [open, bookingId]);

  useTrackRecentlyViewedItem(open && booking?.id ? booking : null);

  // delete
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteTypedText, setDeleteTypedText] = useState('');
  const [deleteSnackbarOpen, setDeleteSnackbarOpen] = useState(false);
  const [deleteSnackbarMessage, setDeleteSnackbarMessage] = useState('');
  const [deleteAssociatedSurvey, setDeleteAssociatedSurvey] = useState(false);

  // permissions
  const userCanUpdateBooking =
    user.type === userTypes.Admin ||
    ((user.type === userTypes.Manager ||
      (user.type === userTypes.ExternalUser &&
        !booking.affiliateSelected &&
        status !== bookingStatuses.Autocompleted.value &&
        status !== bookingStatuses.Completed.value)) &&
      permissions.includes(CORE_PERMISSIONS.BOOKING_WRITE));

  // Define your state for dialog
  const [collisionDialog, setCollisionDialog] = useState({
    open: false,
    title: '',
    content: null
  });

  const generateSlotFromStartValues = useCallback((service, date, startHours, startMinutes) => {
    const slot = {};
    slot.start = date ? dayjs(date).hour(startHours).minute(startMinutes).toDate() : dayjs().hour(7).minute(startMinutes).toDate();
    slot.end = dayjs(slot.start).add(service.duration, 'minute').toDate();
    return slot;
  }, []);

  useEffect(() => {
    if (affiliateSelected && selectedService && !Number.isNaN(startHours) && !Number.isNaN(startMinutes)) {
      const slot = generateSlotFromStartValues(selectedService, date, startHours, startMinutes);
      setNewTimeSlot(slot);
      setOldTimeSlot(slot);
    }
  }, [affiliateSelected, selectedService, startHours, startMinutes, generateSlotFromStartValues, date]);

  const handleDeleteConfirmation = () => {
    setDeleteConfirmationOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteTypedText('');
    setDeleteConfirmationOpen(false);
    setDeleteAssociatedSurvey(false);
  };

  const isSpecificManagerOptionSelected = useMemo(
    () =>
      newManagerOption === managerLocationOptions.specific.value || (!newManagerOption && !anyManagerAtLocation && Boolean(currentManager)),
    [newManagerOption, anyManagerAtLocation, currentManager]
  );

  const currentlySelectedManager = useMemo(
    () => (changeManager && newManager ? newManager : currentManager),
    [changeManager, newManager, currentManager]
  );

  const getManagerSchedulesForLocation = useCallback((manager, locationId) => {
    if (!manager || !manager.Schedules || !locationId) {
      return null;
    }
    return filterManagerSchedulesByLocation(manager.Schedules, locationId);
  }, []);

  const dayIsDisabled = useCallback(
    (date, location) => {
      let dateUnavailable;
      if (location?.id === currentLocation?.id) {
        dateUnavailable = dayIsUnavailable(date, location, currentLocationUnavailableDays);
      } else {
        dateUnavailable = dayIsUnavailable(date, location);
      }

      // const isInvalidWorkday = !dayIsValidWorkday(date, location);

      let isInvalidForManagerSchedule = false;
      if (isSpecificManagerOptionSelected) {
        const managerSchedules = getManagerSchedulesForLocation(currentlySelectedManager, location?.id);
        isInvalidForManagerSchedule =
          managerSchedules &&
          !dateIsValidPerManagerSchedules({
            dayjsdate: date,
            managerSchedules
          });
      }

      return dateUnavailable || isInvalidForManagerSchedule;
    },
    [
      currentLocation,
      currentLocationUnavailableDays,
      isSpecificManagerOptionSelected,
      currentlySelectedManager,
      getManagerSchedulesForLocation
    ]
  );

  const isBookingSafe = async () => {
    if (!location || newStatus?.value !== bookingStatuses.Upcoming.value) {
      // booking with no location cannot have collision
      // booking updated to status other than upcoming cannot have collision
      return true;
    }

    let startDate;
    let endDate;
    if (!newTimeSlot) {
      const newSlot = generateSlotFromStartValues(selectedService, date, startHours, startMinutes);
      startDate = dayjs(newSlot?.start);
      endDate = dayjs(newSlot?.end);
    } else {
      startDate = dayjs(newTimeSlot?.start);
      endDate = dayjs(newTimeSlot?.end);
    }

    if (startDate && endDate && location?.id) {
      const date = getIsoDate(startDate);
      let openingHours = null;

      try {
        // get location opening hours
        const res = await callAzureFunction({
          url: 'opening-hours/available-times',
          method: 'POST',
          data: {
            startDate: date,
            endDate: date,
            locationIds: [location.id]
          },
          appDataSourceId
        });
        const { locationOpeningHoursPerDate } = res.data;
        openingHours = locationOpeningHoursPerDate[location.id]?.[date] ?? [];
      } catch (error) {
        handleError(error);
      }

      if (Array.isArray(openingHours)) {
        const withinLocationOpeningHour = openingHours.find((oh) => {
          const ohStart = dayjs(`${date} ${oh.opens}`);
          const ohEnd = dayjs(`${date} ${oh.closes}`);

          return dayjs(startDate).isSameOrAfter(ohStart) && dayjs(endDate).isSameOrBefore(ohEnd);
        });

        if (!withinLocationOpeningHour) {
          setCollisionDialog({
            open: true,
            title: '',
            content: <LocationUnavailableContent location={location} date={date} openingHours={openingHours} />
          });
          return false;
        }
      }
    }

    const checkForCollision = async () => {
      let result;
      const managerId = newManager?.id || currentManager?.id;

      try {
        result = await callAzureFunctionPublic({
          url: '/public/bookings-find-collision',
          method: 'get',
          params: {
            managerId,
            locationId: location?.id,
            bookingId,
            // startDate: dayjs(startDate).format(),
            // endDate: dayjs(endDate).format(),
            date: dayjs(changeDateTime || changeDateTimeAffiliate ? newDate : booking?.date).format('YYYY-MM-DD'),
            startHours: dayjs(startDate).hour(),
            startMinutes: dayjs(startDate).minute(),
            endHours: dayjs(endDate).hour(),
            endMinutes: dayjs(endDate).minute(),
            include: JSON.stringify({
              includeBlockingLocationEvents: true,
              includeBlockingManagerAtLocationEvents: Boolean(managerId)
            })
          },
          appDataSourceId
        });

        return result;
      } catch (error) {
        result = error.response?.data ?? {};
      }

      const collisions = [];
      if (Array.isArray(result?.data?.conflictingBookings)) {
        result.data.conflictingBookings.forEach((booking) =>
          collisions.push({
            ...booking,
            start: dayjs(booking.date).startOf('day').hour(booking.startHours).minute(booking.startMinutes),
            end: dayjs(booking.date).startOf('day').hour(booking.endHours).minute(booking.endMinutes),
            type: calendarEventTypes.booking
          })
        );
      }
      if (Array.isArray(result?.data?.conflictingLocationEvents)) {
        result.data.conflictingLocationEvents.forEach((event) =>
          collisions.push({
            ...event,
            start: dayjs(event.date).startOf('day').hour(event.startHours).minute(event.startMinutes),
            end: dayjs(event.date).startOf('day').hour(event.endHours).minute(event.endMinutes),
            type: calendarEventTypes.blockedSlot
          })
        );
      }
      if (Array.isArray(result?.data?.conflictingManagerAtLocationEvents)) {
        result.data.conflictingManagerAtLocationEvents.forEach((event) =>
          collisions.push({
            ...event,
            start: dayjs(event.date).startOf('day').hour(event.startHours).minute(event.startMinutes),
            end: dayjs(event.date).startOf('day').hour(event.endHours).minute(event.endMinutes),
            type: calendarEventTypes.managerBlockedSlot
          })
        );
      }

      return collisions;
    };

    if (newStatus?.value === bookingStatuses.Upcoming.value || (status === bookingStatuses.Upcoming.value && !newStatus?.value)) {
      const collisions = await checkForCollision();

      if (collisions?.length) {
        setCollisionDialog({
          open: true,
          title: 'Booking Collision',
          content: <BookingsCollisionDialog bookings={collisions} />
        });
        return false;
      }
    }

    return true;
  };

  const deleteBooking = useCallback(
    async (bookingId) => {
      try {
        setLoadingBooking(true);

        await callAzureFunction({
          url: `bookings`,
          method: 'delete',
          params: { bookingId, deleteAssociatedSurvey }
        });

        // don't reload on booking calendar as the websocket message will cause reload instead
        // since getBookings is now an optional prop, getBookings() can only be executed if it is defined in the first place
        if (pageLocation?.pathname !== '/manage-booking-calendar' && Boolean(getBookings)) {
          getBookings();
        }
        setDeleteSnackbarMessage('Booking deleted');
        setDeleteSnackbarOpen(true);
      } catch (error) {
        const errorMessage = handleError(error);
        setDeleteSnackbarMessage(errorMessage);
        setDeleteSnackbarOpen(true);
      }
    },
    [deleteAssociatedSurvey, getBookings, pageLocation?.pathname]
  );

  const handleDelete = () => {
    deleteBooking(bookingId);
    closeSnackbar();
    setOpen(false);
    setDeleteConfirmationOpen(false);
    setDeleteTypedText(null);
    setDeleteAssociatedSurvey(false);
  };

  const formattedDate = useMemo(() => (date ? dayjs(date).format('DD/MM/YYYY') : null), [date]);

  const formattedTime = useMemo(() => {
    if (startHours === null || startMinutes === null || endHours === null || endMinutes === null) return 'TBD';

    const formattedStartTime = dayjs(getDateWithCustomTime(startHours, startMinutes)).format('hh:mm a');
    const formattedEndTime = dayjs(getDateWithCustomTime(endHours, endMinutes)).format('hh:mm a');

    return formattedStartTime && formattedEndTime ? `${formattedStartTime} - ${formattedEndTime}` : null;
  }, [startHours, startMinutes, endHours, endMinutes]);

  const bookingStatus = useMemo(() => getBookingStatus(status, affiliateSelected), [status, affiliateSelected]);

  const getStatusOptions = () => {
    if (affiliateSelected) {
      return Object.values(affiliateBookingStatuses).filter((each) => each.value);
    }
    return Object.values(bookingStatuses);
  };

  const statusToSave = changeDateTime ? bookingStatuses.Upcoming.value : newStatus?.value;
  const hasPendingCustomStatusForNewStatus = Boolean(pendingCustomStatus && pendingCustomStatus.statusValue === statusToSave);
  const hasCustomStatusChanged =
    hasPendingCustomStatusForNewStatus && pendingCustomStatus.customStatusId !== (booking?.CustomStatus?.id ?? null);

  const shouldUpdateBTNRender = () => {
    const isChangeInTimeSlot = newTimeSlot && newTimeSlot?.start?.valueOf() !== oldTimeSlot?.start?.valueOf();
    const hasValuesChanged = Boolean(
      (selectedService && selectedService?.id !== currentService?.id) ||
      (newLocation && newLocation?.id !== currentLocation?.id) ||
      newManagerOption ||
      newManager ||
      isChangeInTimeSlot ||
      (affiliateSelected && newDate && dayjs(newDate).format('YYYY-MM-DD') !== (date ? dayjs(date).format('YYYY-MM-DD') : '')) ||
      // Check if user toggled between autocomplete and manual input
      isSelectFromSystemAffiliateLocations !== !affiliateLocationState.isManualEntry ||
      // Check if manual location text has changed
      (affiliateLocationState.isManualEntry && affiliateLocationState.manualLocationText !== affiliateLocation) ||
      // Check if manual timezone has changed
      (affiliateLocationState.isManualEntry && affiliateLocationState.manualTimezone !== (specifiedAffiliateTimezone || '')) ||
      // Check if system affiliate location has changed
      (!affiliateLocationState.isManualEntry && affiliateLocationState.systemLocation?.id !== currentLocation?.id) ||
      affiliateNotesHasChanges ||
      newCompany?.id !== currentCompany?.id ||
      newLocalCompany?.id !== currentLocalCompany?.id ||
      Boolean(Boolean(newStatus?.value) && newStatus?.value !== booking?.status) ||
      hasCustomStatusChanged
    );

    // handles case where time picker has empty value or has invalid HH:MM:AMPM due to user input
    if (affiliateSelected && isChangeInTimeSlot && (!newTimeSlot?.start || isNaN(newTimeSlot?.start?.valueOf()))) {
      return false;
    }

    if (enableStatusUpdate) {
      if (bookingStatus && newStatus && bookingStatus.value !== newStatus?.value) {
        return true;
      }
      if (newStatus) {
        return hasValuesChanged;
      }
    }

    return hasValuesChanged;
  };

  const isStatusFromAutocompletedToUpcoming = Boolean(
    status === bookingStatuses.Autocompleted.value && newStatus?.value.getTime === bookingStatuses.Upcoming.value
  );

  const canUpdateBooking = () => {
    if (isStatusFromAutocompletedToUpcoming) {
      // Allow updating of booking status from Autocompleted to Upcoming if the following conditions are met:
      // a) the change date time workflow has been selected as per changeDateTime boolean state
      // b) a new time slot has been changed
      const hasTimeSlotChanged = Boolean(newTimeSlot);
      return changeDateTime && hasTimeSlotChanged;
    }
    return true;
  };

  const getBooking = useCallback(async () => {
    try {
      setLoadingBooking(true);
      const response = await callAzureFunction({
        url: `/bookings/${bookingId}`,
        method: 'get',
        appDataSourceId
      });
      setBooking(response.data);
      setAffiliateNotes(response.data?.affiliateNotes || '');
      setCurrentManager(response.data?.Manager);
      setNewManager(response.data?.Manager);
      setNewManagers(response.data?.Managers);
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingBooking(false);
    }
  }, [bookingId, appDataSourceId]);

  const getCurrentLocationUnavailableDays = useCallback(async (locationId) => {
    if (!locationId) return;

    try {
      const response = await callAzureFunction({
        url: `locations/${locationId}`,
        method: 'get'
      });

      setCurrentLocationUnavailableDays(response.data?.UnavailableDays);
    } catch (error) {
      handleError(error);
    }
  }, []);

  const getManagerSchedule = useCallback(
    async (managerId) => {
      try {
        const response = await callAzureFunction({
          url: `managers/${managerId}`,
          method: 'get'
        });

        // Update the manager with their schedules
        if (newManager?.id === managerId) {
          setNewManager(response.data);
        } else if (currentManager?.id === managerId) {
          setCurrentManager(response.data);
        }
      } catch (error) {
        handleError(error);
      }
    },
    [newManager, currentManager]
  );

  useEffect(() => {
    if (open && bookingId) {
      getBooking();
    }
  }, [open, bookingId, getBooking]);

  useEffect(() => {
    if (open) {
      setNewStatus(bookingStatus || null);
      setPendingCustomStatus(null);
    }
  }, [open, bookingStatus]);

  const handleClose = () => {
    setLoadingBooking(true);
    dispatch(clearOtherData());
    // reset service
    setSelectedService(null);
    setChangeService(false);
    setDisableChangeService(false);
    // reset location
    setNewLocation(null);
    // Reset the Affiliate State to default values
    setAffiliateLocationState({
      // Holds the selected location object from the Autocomplete
      systemLocation: null,
      // Holds the free-text value for a manually specified location
      manualLocationText: affiliateLocation || '',
      // Holds the selected IANA timezone for a manually specified location
      manualTimezone: specifiedAffiliateTimezone || '',
      // Toggles between Autocomplete and TextField
      isManualEntry: !isSelectFromSystemAffiliateLocations
    });
    setChangeLocation(false);
    setDisableChangeLocation(false);
    // reset manager
    setNewManager(null);
    setNewManagers([]);
    setNewManagerOption(null);
    setChangeManager(false);
    setDisableChangeManager(false);
    setAnchorElAddManagersMenu(null);
    // reset date time
    setNewDate(dayjs());
    setNewTimeSlot(null);
    setAvailableTimeSlots([]);
    setChangeDateTime(false);
    setChangeDateTimeAffiliate(false);
    setDisableChangeDateTime(false);
    // reset company
    setNewCompany(null);
    setNewLocalCompany(currentLocalCompany || null);
    // error
    setError(null);
    // calendar event states
    setManagerHasGoogleCalendar(false);
    setManagerHasOutlookCalendar(false);
    if (updatingBooking) {
      setOpen(true);
      setLoadingBooking(false);
    } else {
      setOpen(false);
      setLoadingBooking(false);
    }
  };

  const getGoogleCalendar = useCallback(
    async ({ subject }) => {
      if (!subject) {
        setManagerHasGoogleCalendar(false);
        return;
      }

      try {
        await callAzureFunction({
          method: 'get',
          url: 'google-calendar/calendars',
          params: { subject },
          appDataSourceId
        });
        setManagerHasGoogleCalendar(true);
      } catch (error) {
        setManagerHasGoogleCalendar(false);
        handleError(error);
      }
    },
    [appDataSourceId]
  );

  const getOutlookCalendar = useCallback(
    async ({ userPrincipalName }) => {
      if (!userPrincipalName) {
        setManagerHasOutlookCalendar(false);
        return;
      }

      try {
        const response = await callAzureFunction({
          method: 'get',
          url: 'outlook-calendar/calendars',
          params: { userPrincipalName },
          appDataSourceId
        });

        const { canAccessOutlookCalendar } = response?.data || {};
        setManagerHasOutlookCalendar(canAccessOutlookCalendar);
      } catch (error) {
        setManagerHasOutlookCalendar(false);
        handleError(error);
      }
    },
    [appDataSourceId]
  );

  useEffect(() => {
    const selectedManager = changeManager ? newManager : currentManager;

    if (appConfig?.integrations?.googleCalendar?.calendarApiRead) {
      getGoogleCalendar({ subject: selectedManager?.emailAddress });
    }
    if (appConfig?.integrations?.outlookCalendar?.calendarApiRead) {
      getOutlookCalendar({ userPrincipalName: selectedManager?.emailAddress });
    }
  }, [currentManager, newManager, changeManager, appConfig, getGoogleCalendar, getOutlookCalendar]);

  const getTimeSlots = useCallback(async () => {
    try {
      startLoadingTimeSlots();

      const selectedManager = currentlySelectedManager;
      const timeSlotInterval = enableTimeSlotInterval ? await getTimeSlotInterval(null, location, defaultTimeSlotInterval) : 0;

      const res = await callAzureFunctionPublic({
        url: '/public/booking/available-time-slots',
        method: 'POST',
        data: {
          appDataSourceId,
          date: getIsoDate(dayjs(newDate)),
          serviceId: selectedService?.id,
          locationId: location?.id,
          managerId: selectedManager?.id,
          // TODO: consider moving into endpoint
          timeSlotInterval,
          shiftTimeZone: dayjs.tz.guess()
        }
      });
      const timeSlots = res.data.availableTimeSlots;

      setAvailableTimeSlots(timeSlots);
    } catch (error) {
      handleError(error);
    } finally {
      stopLoadingTimeSlots();
    }
  }, [currentlySelectedManager, location, newDate, selectedService, appDataSourceId, defaultTimeSlotInterval, enableTimeSlotInterval]);

  useEffect(() => {
    // get time slot if the user views time slots or changes date while viewing time slots
    // affiliate bookings don't use time slots currently so can ignore changeDateTimeAffiliate
    if (changeDateTime) {
      getTimeSlots();
    }
  }, [getTimeSlots, newDate, changeDateTime]);

  const [currentMonth, setCurrentMonth] = useState(newDate ? dayjs(newDate).startOf('month') : dayjs().startOf('month'));
  const [loadingAvailableDates, setLoadingAvailableDates] = useState(true);
  const [availableDates, setAvailableDates] = useState(null);

  const getAvailableDates = useCallback(async () => {
    try {
      const serviceId = selectedService?.id;
      const locationId = location?.id;

      if (!currentMonth || !locationId || !serviceId) return;
      setLoadingAvailableDates(true);

      const res = await callAzureFunctionPublic({
        url: '/public/booking/available-dates',
        method: 'POST',
        data: {
          appDataSourceId,
          startDate: getIsoDate(dayjs(currentMonth).startOf('month')),
          endDate: getIsoDate(dayjs(currentMonth).endOf('month')),
          serviceId,
          locationId,
          managerId: currentlySelectedManager?.id
        }
      });
      setAvailableDates(res.data.availableDates);
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingAvailableDates(false);
    }
  }, [currentMonth, appDataSourceId, selectedService?.id, location?.id, currentlySelectedManager?.id]);

  useEffect(() => {
    if (!changeDateTime && !changeDateTimeAffiliate) return;
    getAvailableDates();
  }, [changeDateTime, changeDateTimeAffiliate, getAvailableDates]);

  useEffect(() => {
    if (!affiliateSelected) {
      if (isSpecificManagerOptionSelected) {
        const managerSchedules = getManagerSchedulesForLocation(currentlySelectedManager, location?.id);
        const earliestDate = getEarliestAvailableDate(location, managerSchedules);
        setNewDate(earliestDate);
      } else {
        setNewDate(getEarliestAvailableDate(location));
      }
    }
  }, [affiliateSelected, location, isSpecificManagerOptionSelected, currentlySelectedManager, getManagerSchedulesForLocation]);

  const getAvailableManagers = useCallback(async () => {
    try {
      setLoadingAvailableManagers(true);

      const response = await callAzureFunction({
        url: 'managers-by-location',
        method: 'get',
        params: { locationId: location.id },
        appDataSourceId
      });
      const { rows } = response.data;

      // get managers that are available to selected service and location
      const availableManagers = rows.filter(
        (manager) => manager.canTakeAllServices || manager.Services.some((managersService) => managersService.id === selectedService?.id)
      );

      setAvailableManagers(availableManagers);
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingAvailableManagers(false);
    }
  }, [selectedService, location, appDataSourceId]);

  const handleLocationSwitch = (event) => {
    const { checked } = event.target;

    if (bookingPageInvoice && checked) {
      const status = bookingPageInvoice.status;
      let error;
      switch (status) {
        case INVOICE_STATUS.AWAITING_PAYMENT.id:
          error = `You need to void the existing invoice ${bookingPageInvoice.invoiceNumber} associated with either service or location before changing it`;
          break;
        case INVOICE_STATUS.PAID.id:
          error = `You need to refund the existing invoice ${bookingPageInvoice.invoiceNumber} associated with either service or location before changing it`;
          break;
        default:
          break;
      }
      setInvoiceError(error);
    }

    if (!respondentAvailabilityIsKnown) {
      setNewStatus(checked ? bookingStatuses.Upcoming : null);
    }

    setDisableChangeService(checked);
    setChangeService(checked);
    if (!affiliateSelected) {
      setDisableChangeManager(checked);
      setChangeManager(checked);
      setDisableChangeDateTime(checked);
      setChangeDateTime(checked);

      setNewManagerOption(null);
      setNewManager(null);
      setNewDate(null);
      setNewTimeSlot(null);
    }

    if (!checked) {
      setError(null);
      setNewLocation(null);
      setInvoiceError(null);
    }

    setChangeLocation(checked);
  };

  const handleServiceSwitch = (event) => {
    const { checked } = event.target;

    if (bookingPageInvoice && checked) {
      const status = bookingPageInvoice.status;
      let error;
      switch (status) {
        case INVOICE_STATUS.AWAITING_PAYMENT.id:
          error = `You need to void the existing invoice ${bookingPageInvoice.invoiceNumber} associated with either service or location before changing it`;
          break;
        case INVOICE_STATUS.PAID.id:
          error = `You need to refund the existing invoice ${bookingPageInvoice.invoiceNumber} associated with either service or location before changing it`;
          break;
        default:
          break;
      }
      setInvoiceError(error);
    }

    // if manually toggling a service change, don't allow changing others
    if (respondentAvailabilityIsKnown) {
      setDisableChangeLocation(checked);
      setChangeLocation(false);
      setDisableChangeManager(checked);
      setChangeManager(false);
      setDisableChangeDateTime(checked);
      setChangeDateTime(false);

      setNewLocation(null);
      setNewManagerOption(null);
      setNewManager(null);
      setNewDate(null);
      setNewTimeSlot(null);
    } else {
      handleLocationSwitch(event);
    }

    if (!checked) {
      setError(null);
      setInvoiceError(null);
      setSelectedService(null);
    }

    setChangeService(checked);
  };

  const handleDateTimeSwitch = async (event) => {
    const { checked } = event.target;

    if (checked) {
      setNewStatus(bookingStatuses.Upcoming);
      // Only fetch current locations unavailable days when the user toggles date/time selection, and they haven't been loaded before
      const selectedLocationId = location?.id;
      const isSameLocation = selectedLocationId != null && selectedLocationId === currentLocation?.id;

      if (isSameLocation && !location?.UnavailableDays && !currentLocationUnavailableDays && selectedLocationId) {
        await getCurrentLocationUnavailableDays(selectedLocationId);
      }

      const selectedManager = currentlySelectedManager;
      if (selectedManager?.id && (!selectedManager.Schedules || selectedManager.Schedules.length === 0)) {
        await getManagerSchedule(selectedManager.id);
      }
    } else {
      setError(null);
      setNewTimeSlot(null);
    }

    setChangeDateTime(checked);
  };

  const handleManagerSwitch = (event) => {
    const { checked } = event.target;

    if (!checked) {
      setError(null);
      setNewManagerOption(null);
      setNewManager(null);
    }
    if (!respondentAvailabilityIsKnown) {
      handleDateTimeSwitch(event);
    }

    setChangeManager(checked);
  };

  const handleAffiliateDateTimeSwitch = (event) => {
    const { checked } = event.target;

    if (!checked) {
      setError(null);
      setNewDate(null);
      setNewTimeSlot(oldTimeSlot);
    }

    setChangeDateTimeAffiliate(checked);
  };

  const handleAssignLocalCompanyToRemoteBooking = async () => {
    if (!booking.id || !isAppDataSourceRemote) {
      return;
    }
    try {
      await Promise.all([
        callAzureFunction({
          url: `link-company-to-booking`,
          method: 'post',
          params: { bookingId: booking.id, companyId: newLocalCompany?.id },
          appDataSourceId
        }),
        callAzureFunction({
          url: `link-company-to-respondent`,
          method: 'post',
          params: { respondentId: respondent.id, companyId: newLocalCompany?.id },
          appDataSourceId
        })
      ]);

      enqueueSnackbar('Company successfully assigned', {
        variant: 'success',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right'
        }
      });

      await getBookings();
    } catch (error) {
      enqueueSnackbar('Failed to assign company', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right'
        }
      });
      console.error('Error assigning company:', error);
    }
  };

  const attemptUpdateBooking = async (options = { unsafe: false }) => {
    try {
      setError(null);
      setHasAttemptedSubmit(true);
      setUpdatingBooking(true);
      const errors = [];

      const updatingToService = selectedService || currentService;
      const updatingToLocation = newLocation || currentLocation;

      if (affiliateSelected) {
        if (!affiliateLocationState.isManualEntry && affiliateLocationState.systemLocation === null) {
          errors.push('Location is required');
        }

        if (affiliateLocationState.isManualEntry && !affiliateLocationState.manualLocationText.trim()) {
          errors.push('Location is required');
        }

        if (
          affiliateLocationState.isManualEntry &&
          affiliateLocationState.manualLocationText.trim() &&
          !affiliateLocationState.manualTimezone
        ) {
          errors.push('Timezone is required for a manually specified location');
          if (!changeLocation) {
            setChangeLocation(true);
          }
        }
      }

      // change manager checks
      if (changeManager) {
        if (!newManagerOption) {
          errors.push(`${managerEntityName} option is required for changing ${managerEntityName}`);
        }

        if (requireAssignedManager) {
          if (newManagerOption !== managerLocationOptions.specific.value) {
            const selectedOptionLabel =
              Object.values(managerLocationOptions)
                .find((option) => option.value === newManagerOption)
                ?.getLabel(managerEntityName) ?? '';
            errors.push(`"${selectedOptionLabel}" is not a valid selection for this ${bookingEntityName}`);
          }
          if (!newManager) {
            errors.push(`Select a ${managerEntityName}`);
          }
        } else if (newManagerOption === managerLocationOptions.specific.value && !newManager) {
          errors.push(`Specific ${managerEntityName} is required for changing ${managerEntityName}`);
        }
      }
      // reschedule checks
      const isNewDateValid = Boolean(newDate) && dayjs(newDate).isValid();
      if (changeDateTime) {
        if (!isNewDateValid || !newTimeSlot) {
          errors.push('Date and Time are required for rescheduling');
        }
        if (isNewDateValid && dayIsDisabled(newDate, location)) {
          errors.push('Location is not available on selected Date');
        }
        if (isNewDateValid && newTimeSlot && !isTimeSlotInLocationFuture(newDate, newTimeSlot, location)) {
          errors.push('Selected Date/Time has already passed at the location');
        }
      } else if (changeDateTimeAffiliate && !isNewDateValid) {
        errors.push('Date is required for rescheduling');
      }

      if (errors.length) {
        setError(errors.join('. '));
      } else {
        setError(null);

        const data = {
          status: newStatus?.value || null,
          companyId: newCompany?.id,
          mode
        };

        if (hasPendingCustomStatusForNewStatus) {
          data.customStatusId = pendingCustomStatus.customStatusId;
        }

        if (options.unsafe) {
          data.updateUnsafeWithoutChecking = true;
        }
        if (options.cancellationInitiator) {
          data.cancellationInitiator = options.cancellationInitiator;
          data.cancellationComment = options.cancellationComment;
          if (options.cancellationRequestedAt) {
            data.cancellationRequestedAt = options.cancellationRequestedAt;
          }
        }
        if (changeService) {
          data.date = date;
          data.service = updatingToService;
          data.startHours = startHours;
          data.startMinutes = startMinutes;
          const startTime = dayjs(date).hour(startHours).minute(startMinutes);
          data.endHours = dayjs(startTime).add(updatingToService.duration, 'minute').hour();
          data.endMinutes = dayjs(startTime).add(updatingToService.duration, 'minute').minute();
        }
        if (changeLocation) {
          data.location = updatingToLocation;
        }
        if (changeManager) {
          data.managerOption = newManagerOption;
          data.manager = newManager;
          data.managerIds = newManagerOption === 'specificManager' ? newManagers.map((m) => m.id) : [];
        }

        // passed to get busy times of a manager as per Outlook calendar event
        data.timezone = changeLocation ? updatingToLocation?.timezone : location?.timezone;

        if (changeDateTime) {
          // date and time
          data.date = dayjs(newDate).format('YYYY-MM-DD');
          data.startHours = dayjs(newTimeSlot.start).hour();
          data.startMinutes = dayjs(newTimeSlot.start).minute();
          data.endHours = dayjs(newTimeSlot.end).hour();
          data.endMinutes = dayjs(newTimeSlot.end).minute();
          data.respondentAvailabilityIsKnown = true;
          data.status = bookingStatuses.Upcoming.value;
        } else if (changeDateTimeAffiliate) {
          data.date = dayjs(newDate).format('YYYY-MM-DD');
          if (newTimeSlot) {
            data.startHours = dayjs(newTimeSlot.start).hour();
            data.startMinutes = dayjs(newTimeSlot.start).minute();
            data.endHours = dayjs(newTimeSlot.end).hour();
            data.endMinutes = dayjs(newTimeSlot.end).minute();
          } else if (!oldTimeSlot) {
            data.startHours = dayjs(defaultStartTime).hour();
            data.startMinutes = dayjs(defaultStartTime).minute();
            data.endHours = dayjs(defaultStartTime).add(selectedService?.duration, 'minute').hour();
            data.endMinutes = dayjs(defaultStartTime).add(selectedService?.duration, 'minute').minute();
          }
          data.respondentAvailabilityIsKnown = true;
        }

        // To improve: use the same name for affiliate location in the frontend and backend to avoid this mapping
        if (affiliateSelected && affiliateLocationState.isManualEntry) {
          data.specifiedAffiliateLocation = affiliateLocationState.manualLocationText;
          data.specifiedAffiliateTimezone = affiliateLocationState.manualTimezone;
          data.selectAffiliateFromDropdown = false;
          data.location = null;
        } else if (affiliateSelected && !affiliateLocationState.isManualEntry) {
          data.location = affiliateLocationState.systemLocation;
          data.selectAffiliateFromDropdown = true;
          data.specifiedAffiliateLocation = null;
          data.specifiedAffiliateTimezone = null;
        }

        if (affiliateNotes.trim()) {
          data.affiliateNotes = affiliateNotes;
        }

        let updateUrl;
        if (user.type === userTypes.Admin) {
          updateUrl = `/bookings/${booking.id}`;
        } else if (user.type === userTypes.Manager || user.type === userTypes.ExternalUser) {
          updateUrl = `/bookings/${booking.id}/as-manager`;
        }

        if (isAppDataSourceRemote && newLocalCompany?.id !== currentLocalCompany?.id) {
          await handleAssignLocalCompanyToRemoteBooking();
        }

        const response = await callAzureFunction({ url: updateUrl, method: 'put', data, appDataSourceId });

        const targetStatus = [bookingStatuses.Canceled.value, bookingStatuses.DidNotAttend.value, affiliateBookingStatuses.Confirmed.value];
        const changeInStatus = targetStatus.includes(newStatus?.value);

        const shouldBookingActionSnackbarAppear =
          changeLocation || changeService || changeDateTime || changeDateTimeAffiliate || changeInStatus;

        if (shouldBookingActionSnackbarAppear) {
          const locationChanged = Boolean(currentLocation?.id !== newLocation?.id && changeLocation);
          const serviceChanged = Boolean(currentService?.id !== selectedService?.id && changeService);

          const messageType = getSnackbarMessageType({
            locationChanged,
            serviceChanged,
            changeInDateTime: affiliateSelected ? changeDateTimeAffiliate : changeDateTime,
            changeInStatus,
            status: newStatus?.value
          });

          enqueueSnackbar({
            variant: 'BOOKING_ACTION',
            autoHideDuration: 50000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right'
            },
            variantProps: {
              bookingId: booking.id,
              respondent: booking.Respondent,
              affiliateSelected: affiliateSelected,
              messageType
            }
          });
        }

        const { caseCreationAttempted, caseCreated, caseCreationError } = response.data;

        if (caseCreationAttempted) {
          let message;
          let variant;

          if (caseCreated) {
            variant = 'success';
            message = 'A case has been successfully created in Iinsight';
          } else {
            variant = 'error';
            message = `Failed attempt to create a case in Iinsight. Error info: ${caseCreationError}`;
          }

          enqueueSnackbar({
            variant,
            message: <Typography sx={{ maxWidth: 500 }}>{message}</Typography>,
            anchorOrigin: { vertical: 'top', horizontal: 'right' }
          });
        }

        handleClose();
        // don't reload booking on manage booking calendar, as the websocket message will cause reload here already
        if (pageLocation?.pathname !== '/manage-booking-calendar' || enableStatusUpdate === false) {
          getBookings();
        }
      }
    } catch (error) {
      setError(handleError(error));

      const { isManagerNotAvailable } = error.response?.data?.data || {};
      if (isManagerNotAvailable) {
        setCollisionDialog({
          open: true,
          title: 'Conflict with Manager Schedule',
          content: <ManagerScheduleConflictDialog />
        });
      }
    } finally {
      setUpdatingBooking(false);
    }
  };

  const handleUpdateBookingButton = async () => {
    const bookingIsSafe = await isBookingSafe();
    if (!bookingIsSafe) {
      return;
    }

    const { proceed, cancellationInitiator, cancellationComment, cancellationRequestedAt } = await resolveCancellationInitiator({
      bookingId: booking.id,
      nextStatus: newStatus?.value,
      currentStatus: status,
      // This submission can reassign the company alongside the status, and the charge logic runs
      // against the new one - so the gate has to be asked about that company, not the current one.
      targetCompanyId: newCompany?.id
    });

    if (!proceed) {
      return;
    }

    // cache the answer (both undefined when no prompt was needed) so the collision-retry
    // path below reuses it instead of asking a second time. Kept as one object so the retry
    // cannot send an initiator without the reason that justified it.
    setResolvedCancellationAnswer({ cancellationInitiator, cancellationComment, cancellationRequestedAt });
    await attemptUpdateBooking({ unsafe: false, cancellationInitiator, cancellationComment, cancellationRequestedAt });
  };

  const handleSendReminder = async () => {
    setOpen(false);
    setViewEmailBookingDialogOpen(true);
  };

  const handleOpenChangeBookingTypeDialog = () => {
    setOpen(false);
    setOpenChangeBookingTypeDialog(true);
  };

  const setNewServiceHelper = (newService) => {
    if (changeLocation) {
      setNewLocation(null);
      setNewManagerOption(null);
      setNewManager(null);
    }
    setSelectedService(newService);
  };

  const setNewLocationHelper = (newLocation) => {
    if (changeLocation) {
      setNewManagerOption(null);
      setNewManager(null);
      if (!newLocation?.Services.some((service) => service.id === selectedService?.id)) {
        // deselect the service if it does not belong to the location
        setSelectedService(null);
      }
    }
    setNewLocation(newLocation);
  };

  const renderLocationDetails = () => {
    // Possibilities:
    // 1 - Location from system selected
    // 2 - Affiliate Location from system selected
    // 3 - Affiliate Location from manual text entry
    // 4 - No location (should not happen yet there are some in the system)

    if (affiliateSelected && !isSelectFromSystemAffiliateLocations) {
      return affiliateLocation ? `${affiliateLocation}${specifiedAffiliateTimezone ? ` (${specifiedAffiliateTimezone})` : ''}` : '-';
    }

    if (!currentLocation) {
      return '-';
    }

    const locationString = currentLocation.name || '-';

    if (currentLocation?.deletedAt) {
      return (
        <Tooltip title="This location is currently archived in the system." placement="right">
          <Typography sx={{ ml: 0.5 }}>{`${locationString} (Archived)`}</Typography>
        </Tooltip>
      );
    }

    if (user.type === userTypes.Admin && !isAppDataSourceRemote && currentLocation.id) {
      return (
        <Link to={`/manage-locations/edit-location/${currentLocation.id}`} style={{ marginLeft: '4px' }}>
          {locationString}
        </Link>
      );
    }

    return locationString;
  };

  const renderManagers = () => {
    const isClickable = user.type === userTypes.Admin && !isAppDataSourceRemote;
    const list = Array.isArray(newManagers) && newManagers.length ? newManagers : newManager ? [newManager] : [];

    return (
      <Box display="flex" flexWrap="wrap" gap={0.75} ml={0.75} alignItems="center">
        {list.map((m) => {
          const fullName = `${m.firstName} ${m.lastName}`;

          return isClickable ? (
            <Chip
              key={m.id}
              label={fullName}
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => navigate(`/managers/${m.id}/edit`)}
              onDelete={() => {
                const newList = [...list].filter((manager) => m.id !== manager.id);
                setNewManagerOption(newList.length === 0 ? managerLocationOptions.any.value : managerLocationOptions.specific.value);
                setNewManager(newList[0] ?? null);
                setNewManagers(newList);
                setChangeManager(true);
              }}
            />
          ) : (
            <Chip key={m.id} label={fullName} size="small" variant="outlined" />
          );
        })}

        <Chip
          icon={<AddIcon />}
          label="Add"
          size="small"
          variant="outlined"
          onClick={(event) => setAnchorElAddManagersMenu(event?.currentTarget ?? null)}
        />
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
        {loadingBooking ? (
          <DialogContent>
            <Skeleton variant="rectangular" height={250} />
          </DialogContent>
        ) : (
          <>
            {booking.id ? (
              <>
                <DialogTitle variant="h3">
                  {bookingEntityName} for {respondent ? `${respondent.firstName} ${respondent.lastName}` : '-'}
                </DialogTitle>
                <DialogContent>
                  <Grid container spacing={2} mt={2}>
                    {/* status */}
                    <Grid container size={6} direction="row" alignItems="end">
                      {isAppDataSourceRemote || !booking.id ? (
                        userCanUpdateBooking && enableStatusUpdate && !isAppDataSourceRemote ? (
                          <Grid container alignItems="center" direction="row" size={12} spacing={0} my={0}>
                            <Typography>Status: </Typography>
                            <Autocomplete
                              id="select-new-status"
                              options={getStatusOptions()}
                              isOptionEqualToValue={(option, value) => option.value === value.value}
                              getOptionLabel={(option) => (option ? option.label : '')}
                              value={newStatus}
                              onChange={(_, value) => setNewStatus(value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  slotProps={{
                                    input: {
                                      ...params.InputProps,
                                      startAdornment: newStatus ? (
                                        <FiberManualRecord style={{ color: newStatus.color, fontSize: 18 }} />
                                      ) : null
                                    }
                                  }}
                                />
                              )}
                              size="small"
                              renderOption={(props, option) => (
                                <li {...props}>
                                  <FiberManualRecord style={{ color: option.color, fontSize: 18, marginRight: '10px' }} />
                                  {option.label}
                                </li>
                              )}
                              style={{ marginLeft: '5px', minWidth: '200px' }}
                            />
                          </Grid>
                        ) : (
                          <Typography>
                            Status: {bookingStatus ? <span style={{ color: bookingStatus.color }}>{bookingStatus.label}</span> : '-'}
                          </Typography>
                        )
                      ) : (
                        <BookingStatusPicker
                          savedStatus={status}
                          savedCustomStatus={booking.CustomStatus ?? null}
                          statusOptions={getStatusOptions()}
                          pendingStatus={newStatus}
                          pendingCustomStatusId={
                            pendingCustomStatus && pendingCustomStatus.statusValue === newStatus?.value
                              ? pendingCustomStatus.customStatusId
                              : undefined
                          }
                          canChangeStatus={Boolean(userCanUpdateBooking && enableStatusUpdate)}
                          canSetCustomStatus={user.type === userTypes.Admin && permissions.includes(CORE_PERMISSIONS.MANAGE_BOOKINGS)}
                          onPendingStatusChange={(statusOption, customStatusId) => {
                            setNewStatus(statusOption);
                            setPendingCustomStatus(
                              customStatusId === undefined ? null : { statusValue: statusOption.value, customStatusId }
                            );
                          }}
                        />
                      )}
                    </Grid>

                    {bookingConfig?.allowAffiliate && user?.type === userTypes.Admin && (
                      <Grid size={{ xs: 12, sm: 6 }} mt={2}>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Button onClick={handleOpenChangeBookingTypeDialog} size="small">
                            Change to {affiliateSelected ? 'Non-affiliate' : 'affiliate'}
                          </Button>
                          <Tooltip
                            title={
                              affiliateSelected
                                ? `Non-affiliate ${bookingsEntityName} are regular ${bookingsEntityName} that have a location and ${managerEntityName} from your AtlasOne system`
                                : `Affiliate ${bookingsEntityName} are ${bookingsEntityName} outside of your normal locations. It has no ${managerEntityName}, and the location does not have to exist in your AtlasOne system`
                            }
                            placement="top"
                          >
                            <InfoIcon sx={{ fontSize: 18 }} color="primary" />
                          </Tooltip>
                        </Stack>
                      </Grid>
                    )}

                    {/* respondent */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography style={{ display: 'flex', alignItems: 'center' }}>
                        {respondentEntityName}:
                        {respondent ? (
                          <>
                            {isAppDataSourceRemote ? (
                              ` ${respondent.firstName} ${respondent.lastName}`
                            ) : (
                              <Link to={`/respondents/view-respondent/${respondent.id}`} style={{ marginLeft: '4px' }}>
                                {`${respondent.firstName} ${respondent.lastName}`}
                              </Link>
                            )}
                          </>
                        ) : (
                          ' -'
                        )}
                      </Typography>
                    </Grid>

                    {/* manager */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography style={{ display: 'flex', alignItems: 'center' }}>
                        {managerEntityName}:{anyManagerAtLocation ? ` Any ${managerEntityName} at Location` : renderManagers()}
                        {!anyManagerAtLocation && !currentManager && ' -'}
                      </Typography>
                    </Grid>

                    {/* location */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography style={{ display: 'flex', alignItems: 'center' }}>Location: {renderLocationDetails()}</Typography>
                    </Grid>

                    {/* service */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography style={{ display: 'flex', alignItems: 'center' }}>
                        Service:
                        {currentService ? (
                          <>
                            {user.type === userTypes.Admin ? (
                              <>
                                {currentService?.deletedAt ? (
                                  <Tooltip title="This service is currently archived in the system." placement="right">
                                    <Typography sx={{ ml: 0.5 }}>{currentService.name} (Archived)</Typography>
                                  </Tooltip>
                                ) : (
                                  <>
                                    {isAppDataSourceRemote ? (
                                      ` ${currentService.name}`
                                    ) : (
                                      <Link to={`/manage-services/edit-service/${currentService.id}`} style={{ marginLeft: '4px' }}>
                                        {`${currentService.name}`}
                                      </Link>
                                    )}
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                {currentService?.deletedAt ? (
                                  <Tooltip title="This service is currently archived in the system." placement="right">
                                    <Typography sx={{ ml: 0.5 }}>{currentService.name} (Archived)</Typography>
                                  </Tooltip>
                                ) : (
                                  <Typography sx={{ ml: 0.5 }}>{currentService.name}</Typography>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          ' - '
                        )}
                      </Typography>
                    </Grid>

                    {/* date */}
                    <Grid size={{ xs: 12, sm: 6 }} alignItems="center" sx={{ display: 'flex' }}>
                      <Typography>Date: {respondentAvailabilityIsKnown ? formattedDate : '-'}</Typography>
                    </Grid>

                    {/* time */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Appointment Time:{' '}
                        {respondentAvailabilityIsKnown ? (
                          formattedTime
                        ) : (
                          <>
                            {respondentConfirmationNotifications ? (
                              <MUILink
                                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}
                                onClick={() => setOpenScheduledNotificationsDialog(true)}
                              >
                                Notification Schedule <ScheduleSend sx={{ verticalAlign: 'middle' }} />
                              </MUILink>
                            ) : (
                              <>-</>
                            )}
                          </>
                        )}
                      </Typography>
                    </Grid>
                    {preferredTimeFrame && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography textTransform="capitalize">
                          Preferred Time Frame: {getPreferredTimeFrameLabel(preferredTimeFrame)}
                        </Typography>
                      </Grid>
                    )}

                    {/* bookingId */}
                    {bookingId && (
                      <Grid size={{ xs: 12, sm: 6 }} alignItems="center" sx={{ display: 'flex' }}>
                        <Typography>AtlasOne Booking No: {bookingId}</Typography>
                      </Grid>
                    )}

                    {/* insightCaseId */}
                    {(enableIinsightCaseNumQuery || iinsightCaseCreationEnabled) && (
                      <Grid size={{ xs: 12, sm: 6 }} alignItems="center" sx={{ display: 'flex' }}>
                        <Typography>iinsight Case Number: {insightCaseId || 'None'}</Typography>
                      </Grid>
                    )}

                    {/* companies */}
                    {companiesFeatureEnabled &&
                      (user.type === userTypes.Admin && canUpdateBookingCompanies && !isAppDataSourceRemote ? (
                        <Grid container size={6} direction="row" alignItems="center" wrap="nowrap">
                          <Typography>Company: </Typography>
                          <AutocompleteWithInfiniteScroll
                            // custom props
                            optionsUrl="companies/autocomplete"
                            optionsHighlight
                            // autocomplete props
                            id="company-select"
                            // allows newCompany to be an empty string but not null
                            value={newCompany}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={(option) => companyLabelBuilder(option)}
                            onChange={(_, value) => setNewCompany(value)}
                            renderInput={(params) => <TextField {...params} />}
                            size="small"
                            fullWidth
                            style={{ marginLeft: '5px', marginRight: '50px', minWidth: '170px' }}
                          />
                        </Grid>
                      ) : (
                        <Grid size={{ xs: 12, sm: 6 }} alignItems="center" sx={{ display: 'flex' }}>
                          <Typography>Company: {currentCompany?.name}</Typography>
                        </Grid>
                      ))}
                    {companiesFeatureEnabled &&
                      isAppDataSourceRemote &&
                      (user.type === userTypes.Admin && canUpdateBookingCompanies ? (
                        <Grid container size={6} direction="row" alignItems="center">
                          <Typography>Local Company:</Typography>
                          <AutocompleteWithInfiniteScroll
                            optionsUrl="companies/autocomplete"
                            optionsHighlight
                            fullWidth
                            name="company"
                            onChange={(_, value) => setNewLocalCompany(value)}
                            renderInput={(params) => <TextField label="Company" {...params} />}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            value={newLocalCompany}
                            getOptionLabel={(option) => `${option.name} (${option.id})`}
                            sx={{ flex: 1 }}
                          />
                        </Grid>
                      ) : (
                        <Grid size={{ xs: 12, sm: 6 }} alignItems="center" sx={{ display: 'flex' }}>
                          <Typography>Local Company: {currentLocalCompany?.name}</Typography>
                        </Grid>
                      ))}

                    {/* created from */}
                    {bookingCreatedFrom && (
                      <Grid size={{ xs: 12, sm: 6 }} alignItems="center" sx={{ display: 'flex' }}>
                        <Typography>Created From: {bookingCreatedFrom}</Typography>
                      </Grid>
                    )}

                    {/* Email Notification */}
                    <EmailEventLogsField
                      open={open}
                      bookingId={bookingId}
                      appDataSourceId={appDataSourceId}
                      bookingEntityName={bookingEntityName}
                    />

                    {/* Affiliate Notes */}
                    {booking.affiliateSelected && (
                      <Grid size={12} alignItems="center">
                        <Stack gap={1}>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography>Affiliate Notes:</Typography>
                            <Tooltip
                              title="This note will be attached to the booking confirmation text if sent after confirming."
                              placement="bottom-start"
                            >
                              <InfoIcon sx={{ fontSize: 18 }} color="primary" />
                            </Tooltip>
                            <Switch checked={viewAffiliateNotes} onChange={(e) => setViewAffiliateNotes(e.target.checked)} />
                          </Stack>
                          {/* Affiliate Notes Editor */}
                          {viewAffiliateNotes && (
                            <>
                              <TipTapEditor data={affiliateNotes} onChange={setAffiliateNotes} />
                            </>
                          )}
                        </Stack>
                      </Grid>
                    )}

                    <Grid size={{ xs: 12, sm: 6 }}>
                      {createdAt && <Typography>Created: {dayjs(createdAt).format('DD/MM/YYYY h:mm a')} </Typography>}
                      {updatedAt && <Typography>Updated: {dayjs(updatedAt).format('DD/MM/YYYY h:mm a')}</Typography>}
                    </Grid>

                    <Grid size={12}>
                      <Divider />
                    </Grid>

                    {generalInvoicingAndPaymentsEnabled && (
                      <Grid size={12}>
                        <Typography>Invoice: </Typography>
                        {invoices?.map((invoice) => (
                          <Stack key={invoice.id} direction="row" spacing={1} alignItems="center">
                            <HtmlTooltip title={invoice.status}>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => setOpenBookingInvoicesDialog(true)}
                                color={INVOICE_STATUS[invoice.status]?.color || 'primary'}
                              >
                                {invoice.number ?? `INV-${invoice.id}`}
                              </Button>
                            </HtmlTooltip>
                            <Chip label={`${invoice.billedAmount} ${invoice.currency}`} />
                          </Stack>
                        ))}
                      </Grid>
                    )}

                    <Grid size={12}>
                      <Divider />
                    </Grid>

                    {!isReadOnly && (
                      <>
                        {userCanUpdateBooking && (
                          <Grid container size={12}>
                            {/* change service */}
                            <Grid size={{ xs: 12, sm: 3 }}>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                <Typography>Select Service?</Typography>
                                <Switch checked={changeService} onChange={handleServiceSwitch} disabled={disableChangeService} />
                              </Stack>
                            </Grid>
                            {/* change location */}

                            <Grid size={{ xs: 12, sm: 3 }}>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                <Typography>Select Location?</Typography>
                                <Switch checked={changeLocation} onChange={handleLocationSwitch} disabled={disableChangeLocation} />
                              </Stack>
                            </Grid>

                            {/* change manager */}
                            {!affiliateSelected && (
                              <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                  <Typography>Select {managerEntityName}?</Typography>
                                  <Switch checked={changeManager} onChange={handleManagerSwitch} disabled={disableChangeManager} />
                                </Stack>
                              </Grid>
                            )}
                            {/* change date/time */}
                            {affiliateSelected ? (
                              <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography>Select Date & Time?</Typography>
                                  <Switch checked={changeDateTimeAffiliate} onChange={handleAffiliateDateTimeSwitch} />
                                </Stack>
                              </Grid>
                            ) : (
                              <Grid size={{ xs: 12, sm: 3 }}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                                  <Typography>Select Date & Time?</Typography>
                                  <Switch checked={changeDateTime} onChange={handleDateTimeSwitch} disabled={disableChangeDateTime} />
                                </Stack>
                              </Grid>
                            )}
                          </Grid>
                        )}
                      </>
                    )}

                    {/* service options for change service */}
                    {changeService && (
                      <Grid size={12}>
                        <Divider />
                        <AutocompleteWithInfiniteScroll
                          // custom props
                          optionsUrl="services/autocomplete"
                          optionsUrlParams={{
                            locationId: location?.id
                          }}
                          optionsHighlight
                          // autocomplete props
                          id="select-service"
                          value={selectedService}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          getOptionLabel={(option) => (option?.id ? `${option.name}` : '')}
                          onChange={(_, value) => setNewServiceHelper(value)}
                          renderInput={(params) => <TextField {...params} label="Service" />}
                          fullWidth
                          appDataSourceId={appDataSourceId}
                          disabled={invoiceError}
                          sx={{ mt: 2 }}
                        />
                      </Grid>
                    )}

                    {/* location options for change location */}
                    {!affiliateSelected && changeLocation && (
                      <Grid size={12}>
                        <Divider />
                        <AutocompleteWithInfiniteScroll
                          // custom props
                          optionsUrl="locations/autocomplete"
                          optionsUrlParams={{
                            include: [LOCATION_TYPE.REGULAR, LOCATION_TYPE.TEMPORARY, LOCATION_TYPE.AREA_BASED]
                          }}
                          optionsHighlight
                          // autocomplete props
                          id="select-location"
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          getOptionLabel={(option) => (option?.id ? `${option.name}` : '')}
                          value={newLocation}
                          onChange={(_, value) => setNewLocationHelper(value)}
                          renderInput={(params) => <TextField {...params} label="Location" />}
                          fullWidth
                          appDataSourceId={appDataSourceId}
                          disabled={invoiceError}
                        />
                      </Grid>
                    )}

                    {[userTypes.Admin, userTypes.Manager].includes(user.type) && affiliateSelected && changeLocation && (
                      <Grid size={12}>
                        <Divider />
                        <Stack spacing={1} sx={{ mt: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {!affiliateLocationState.isManualEntry ? (
                              <AutocompleteWithInfiniteScroll
                                // custom props
                                optionsUrl="locations/autocomplete"
                                optionsUrlParams={{
                                  include: [LOCATION_TYPE.AFFILIATE]
                                }}
                                optionsHighlight
                                // autocomplete props
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                getOptionLabel={(option) => option?.name || ''}
                                value={affiliateLocationState.systemLocation}
                                onChange={(event, newValue) => {
                                  setAffiliateLocationState((prevState) => ({
                                    ...prevState,
                                    systemLocation: newValue
                                  }));
                                }}
                                renderInput={(params) => <TextField {...params} label="Location" />}
                                fullWidth
                                appDataSourceId={appDataSourceId}
                              />
                            ) : (
                              <TextField
                                label="Location"
                                fullWidth
                                value={affiliateLocationState.manualLocationText}
                                onChange={(e) =>
                                  setAffiliateLocationState((prevState) => ({
                                    ...prevState,
                                    manualLocationText: e.target.value
                                  }))
                                }
                              />
                            )}
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={affiliateLocationState.isManualEntry}
                                  onChange={(e) =>
                                    setAffiliateLocationState((prevState) => ({
                                      ...prevState,
                                      isManualEntry: e.target.checked,
                                      ...(e.target.checked ? {} : { manualLocationText: '', manualTimezone: '' })
                                    }))
                                  }
                                />
                              }
                              label="Specify Location"
                            />
                          </Stack>
                          {(affiliateLocationState.isManualEntry || affiliateLocationState.manualLocationText.trim()) && (
                            <TimezoneAutocompleteNzAu
                              value={affiliateLocationState.manualTimezone}
                              onChange={(event, newValue) => {
                                setAffiliateLocationState((prevState) => ({
                                  ...prevState,
                                  manualTimezone: newValue?.value || ''
                                }));
                              }}
                              required
                              error={hasAttemptedSubmit && !affiliateLocationState.manualTimezone}
                            />
                          )}
                        </Stack>
                      </Grid>
                    )}

                    {invoiceError && (
                      <Grid size={12}>
                        <Alert severity="error">{invoiceError}</Alert>
                      </Grid>
                    )}

                    {/* manager options for change manager */}
                    {!affiliateSelected && changeManager && (
                      <Grid size={12}>
                        <Divider />
                        <Grid container spacing={1} mb={2} mt={2}>
                          {Object.values(managerLocationOptions)
                            .filter(
                              (managerOption) => !(requireAssignedManager && managerOption.value === managerLocationOptions.any.value)
                            )
                            .map((managerOption) => (
                              <Grid key={managerOption.value} size={{ xs: 12, sm: 6 }}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    borderColor: newManagerOption === managerOption.value && theme.palette.primary.main,
                                    borderWidth: newManagerOption === managerOption.value && 3
                                  }}
                                  onClick={() => setNewManagerOption(managerOption.value)}
                                >
                                  <Typography variant="h3">{managerOption.getLabel(managerEntityName)}</Typography>
                                </Card>
                              </Grid>
                            ))}
                        </Grid>

                        {/* select manager from available managers */}
                        {newManagerOption === 'specificManager' && (
                          <Autocomplete
                            id="select-manager"
                            loading={loadingAvailableManagers}
                            options={availableManagers}
                            isOptionEqualToValue={(option, value) => option?.id === value.id}
                            getOptionLabel={(option) => (option?.id ? `${option.firstName} ${option.lastName}` : '')}
                            value={newManagers}
                            onChange={(_, value) => {
                              setNewManager(value[0]);
                              setNewManagers(value);
                            }}
                            onOpen={getAvailableManagers}
                            renderInput={(params) => <TextField {...params} label={`Select ${managerEntityName}`} />}
                            fullWidth
                            disabled={!location?.id}
                            multiple
                            disableCloseOnSelect
                          />
                        )}
                      </Grid>
                    )}

                    {/* date and time picker for rescheduling */}
                    {!affiliateSelected && changeDateTime && (
                      <Grid size={12}>
                        <Divider />
                        <Grid container spacing={3}>
                          {/* date picker */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <CustomStaticDatePicker
                              value={newDate}
                              onChange={(value) => setNewDate(value)}
                              onMonthChange={(value) => {
                                setCurrentMonth(value);
                              }}
                              slotProps={{ actionBar: { actions: [] } }}
                              disablePast
                              disabled={loadingTimeSlots || loadingAvailableDates}
                              shouldDisableDate={(value) => {
                                const currentDate = getIsoDate(dayjs(value));
                                return !availableDates?.[currentDate];
                              }}
                            />
                          </Grid>
                          {/* time (slot) picker */}
                          <Grid container size={{ xs: 12, md: 6 }} direction="column" mt={matchesDownMd ? 0 : 12}>
                            <Grid size={6}>
                              {newManagerOption !== managerLocationOptions.any.value && (
                                <Stack direction="row" sx={{ display: 'absolute', mt: -6, mb: 2 }}>
                                  {managerHasGoogleCalendar && (
                                    <HtmlTooltip
                                      disableFocusListener
                                      title={`Google Calendar events from the selected ${managerEntityName} are being considered for the available time slots.`}
                                      placement="top"
                                    >
                                      <IconButton>
                                        <GoogleIcon />
                                      </IconButton>
                                    </HtmlTooltip>
                                  )}
                                  {managerHasOutlookCalendar && (
                                    <HtmlTooltip
                                      disableFocusListener
                                      title={`Outlook Calendar events from the selected ${managerEntityName} are being considered for the available time slots.`}
                                      placement="top"
                                    >
                                      <IconButton>
                                        <MicrosoftIcon />
                                      </IconButton>
                                    </HtmlTooltip>
                                  )}
                                  <Box sx={{ height: 40 }} />
                                </Stack>
                              )}
                            </Grid>
                            <Grid size={12}>
                              {/* time slots */}
                              {loadingTimeSlots ? (
                                <Skeleton variant="rectangular" height={250} />
                              ) : (
                                <>
                                  {availableTimeSlots.length > 0 ? (
                                    <Grid container spacing={1} sx={{ maxHeight: 190, overflow: 'auto' }}>
                                      {availableTimeSlots.map((slot) => (
                                        <Grid key={slot.startText} size={{ xs: 12, sm: 6, md: 4 }}>
                                          <Card
                                            variant="outlined"
                                            sx={{
                                              p: 2,
                                              cursor: 'pointer',
                                              textAlign: 'center',
                                              borderColor: newTimeSlot?.startText === slot.startText && theme.palette.primary.main,
                                              borderWidth: newTimeSlot?.startText === slot.startText && 3
                                            }}
                                            onClick={() => setNewTimeSlot(slot)}
                                          >
                                            <Typography variant="h5">{slot.startText}</Typography>
                                          </Card>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  ) : (
                                    <Typography>No available time slots.</Typography>
                                  )}
                                  {/* custom time picker */}
                                  <Stack sx={{ mt: 3 }}>
                                    <CustomTimePicker
                                      value={newTimeSlot?.start ? dayjs(newTimeSlot.start) : null}
                                      onChange={(value) => {
                                        if (value) {
                                          const start = dayjs(value);
                                          const end = start.add(selectedService?.duration, 'minute');
                                          const timeSlot = {
                                            start,
                                            startText: start.format('hh:mm a'),
                                            end,
                                            endText: end.format('hh:mm a')
                                          };
                                          setNewTimeSlot(timeSlot);
                                        } else {
                                          setNewTimeSlot(null);
                                        }
                                      }}
                                      slots={{ textField: CustomTimeTextField }}
                                    />
                                  </Stack>
                                </>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {/* date and time picker for affiliate */}
                    {affiliateSelected && changeDateTimeAffiliate && (
                      <Grid container size={12}>
                        {/* date picker */}
                        <Grid size={6}>
                          <CustomStaticDatePicker
                            value={newDate}
                            onChange={(value) => setNewDate(value)}
                            slotProps={{ actionBar: { actions: [] } }}
                          />
                        </Grid>

                        {/* time picker */}
                        <Grid size={6} style={{ paddingTop: '16px' }}>
                          <Typography
                            style={{
                              color: '#9e9e9e',
                              lineHeight: 2.66,
                              fontSize: '0.75rem'
                            }}
                          >
                            SELECT START TIME
                          </Typography>

                          <CustomTimePicker
                            value={dayjs(newTimeSlot?.start)}
                            onChange={(newValue) => {
                              setNewTimeSlot({
                                start: newValue,
                                end: dayjs(newValue).add(selectedService?.duration, 'minute').toDate()
                              });
                            }}
                            label="Time"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>

                  {error && (
                    <Grid size={12} mt={1}>
                      <Alert severity="error">
                        <AlertTitle>Error</AlertTitle>
                        {error}
                      </Alert>
                    </Grid>
                  )}
                </DialogContent>
                <DialogActions>
                  {generalInvoicingAndPaymentsEnabled && bookingPageInvoice?.status === INVOICE_STATUS.AWAITING_PAYMENT.id && (
                    <Button startIcon={<Block fontSize="small" />} onClick={() => setOpenVoidDialog(true)}>
                      Void Invoice
                    </Button>
                  )}

                  {generalInvoicingAndPaymentsEnabled && bookingPageInvoice?.status === INVOICE_STATUS.PAID.id && (
                    <Button startIcon={<Undo fontSize="small" />} onClick={() => setOpenInvoicePaymentsDialog(true)}>
                      Refund Invoice
                    </Button>
                  )}

                  <Button onClick={() => setOpenActivityLogsDialog(true)}>Activity Logs</Button>
                  {/* delete booking */}
                  {user.type === userTypes.Admin && !isReadOnly && !isAppDataSourceRemote && (
                    <Button color="error" onClick={handleDeleteConfirmation}>
                      Delete
                    </Button>
                  )}
                  {/* update booking */}
                  {(user.type === userTypes.Admin ||
                    ((user.type === userTypes.Manager || user.type === userTypes.ExternalUser) &&
                      permissions.includes(CORE_PERMISSIONS.BOOKING_WRITE))) &&
                    !isReadOnly && (
                      <Tooltip
                        title="Change into a valid schedule to proceed with the updating"
                        disableHoverListener={canUpdateBooking()}
                        placement="top"
                      >
                        <Button
                          loading={updatingBooking}
                          onClick={handleUpdateBookingButton}
                          disabled={!canUpdateBooking() || !shouldUpdateBTNRender()}
                        >
                          Update {bookingEntityName}
                        </Button>
                      </Tooltip>
                    )}
                  {/* send booking reminder */}
                  {[userTypes.Admin, userTypes.Manager].includes(user.type) &&
                    !isAppDataSourceRemote &&
                    (affiliateSelected || bookingStatus?.value === bookingStatuses.Upcoming.value) && (
                      <Button onClick={handleSendReminder}>Send Notification</Button>
                    )}
                  {/* close dialog */}
                  <Button variant="contained" onClick={handleClose} disabled={updatingBooking}>
                    Close
                  </Button>
                </DialogActions>
              </>
            ) : (
              <Typography>{bookingEntityName} not found</Typography>
            )}
          </>
        )}
      </Dialog>

      <Dialog open={deleteConfirmationOpen} onClose={handleCancelDelete}>
        <DialogTitle>
          <Typography variant="h3">Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h5">
            Type &quot;<strong>DELETE</strong>&quot; to confirm deletion.
          </Typography>
          <TextField
            autoFocus
            type="text"
            fullWidth
            value={deleteTypedText}
            onChange={(event) => setDeleteTypedText(event.target.value.toUpperCase())}
            style={{ paddingTop: '5px' }}
          />
          <FormControlLabel
            control={<Checkbox checked={deleteAssociatedSurvey} onChange={(e) => setDeleteAssociatedSurvey(e.target.checked)} />}
            label={`Also delete any ${surveysEntityName} that are associated with this ${bookingEntityName}?`}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={deleteTypedText !== 'DELETE'} onClick={handleDelete}>
            Confirm Delete
          </Button>
          <Button variant="contained" onClick={handleCancelDelete}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {cancellationInitiatorDialog}

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={deleteSnackbarOpen}
        onClose={() => {
          setDeleteSnackbarOpen(false);
        }}
        message={deleteSnackbarMessage}
        autoHideDuration={3000}
      />

      <Dialog
        open={collisionDialog.open}
        onClose={() => {
          setCollisionDialog({ open: false, title: '', content: '' });
        }}
        maxWidth="xs"
      >
        <DialogTitle>{collisionDialog.title}</DialogTitle>
        <DialogContent>{collisionDialog.content}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCollisionDialog({ open: false, title: '', content: '' });
            }}
          >
            Cancel
          </Button>
          {user.type !== userTypes.ExternalUser && (
            <Button
              variant="contained"
              onClick={() => {
                setCollisionDialog({ open: false, title: '', content: '' });
                // reuse the initiator answer and its reason from the first attempt - both were
                // already resolved (or not needed) before this retry, so don't ask again
                attemptUpdateBooking({ unsafe: true, ...resolvedCancellationAnswer });
              }}
            >
              Continue Anyway
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <EmailBookingDialog
        setViewDialogOpen={setOpen}
        setOpen={setViewEmailBookingDialogOpen}
        open={viewEmailBookingDialogOpen}
        bookingId={bookingId}
      />
      {openChangeBookingTypeDialog && (
        <ChangeBookingTypeDialog
          open={openChangeBookingTypeDialog}
          setOpen={setOpenChangeBookingTypeDialog}
          onClosed={() => setOpen(true)}
          booking={booking}
          onUpdated={() => {
            setOpen(false);
            getBookings();
          }}
        />
      )}
      <ScheduledNotificationsDialog
        open={openScheduledNotificationsDialog}
        setOpen={setOpenScheduledNotificationsDialog}
        bookingId={bookingId}
      />

      <BookingInvoicesDialog
        open={openBookingInvoicesDialog}
        setOpen={setOpenBookingInvoicesDialog}
        invoices={invoices}
        setInvoices={setInvoices}
        setBookings={setBookings}
      />

      <ActivityLogsDialog open={openActivityLogsDialog} setOpen={setOpenActivityLogsDialog} bookingId={bookingId} />

      <VoidDialog
        open={openVoidDialog}
        onClose={() => setOpenVoidDialog(false)}
        onVoid={handleVoid}
        targetInfo={{
          id: bookingPageInvoice?.id ?? '',
          displayIdentifier: `Invoice (${bookingPageInvoice?.invoiceNumber})`
        }}
        loading={loading}
      />

      <InvoicePaymentsDialog
        open={openInvoicePaymentsDialog}
        setOpen={setOpenInvoicePaymentsDialog}
        invoiceIds={[bookingPageInvoice?.id]}
        setDisplayInvoices={setInvoices}
        setBookings={setBookings}
      />

      <PaginatedMenu
        anchorEl={anchorElAddManagersMenu}
        requestConfig={{
          url: `managers/autocomplete`,
          method: 'get',
          params: { excludeExtUsers: true, locationIds: location?.id ? `${location.id}` : '' }
        }}
        getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName}`.trim() || `${managerEntityName} [${option.id}]`}
        onClick={(_event, option) => {
          const exists = newManagers.some((m) => m.id === option.id);
          if (!exists) {
            const newList = [...newManagers, option];
            setNewManager(newList[0]);
            setNewManagers(newList);
            setNewManagerOption(managerLocationOptions.specific.value);
            setChangeManager(true);
          }
          setAnchorElAddManagersMenu(null);
        }}
        onClose={() => setAnchorElAddManagersMenu(null)}
      />
    </>
  );
};

export default ViewBookingDialog;
